import type { ParkingOccupancyEntry } from '@/shared/api';

/**
 * Eine Reservierung als durchgehender Balken über N Tage — dieselbe Form, die der
 * Buchungskalender für eine Buchung nutzt, damit beide Modi dieselbe Sprache sprechen.
 */
export interface ParkingSpan {
  entry: ParkingOccupancyEntry;
  startIndex: number;
  nights: number;
  hasTransitionIn: boolean;
  hasTransitionOut: boolean;
}

/**
 * Schneidet die Reservierungen auf das sichtbare Fenster zu und markiert die Wechseltage.
 *
 * Der Zeitraum ist **halboffen** — `endDate` gehört schon dem Nächsten. Zum Verkaufen ist das
 * richtig; zum Anzeigen ist es zu wenig: bis zur Check-out-Zeit steht real noch das alte Auto
 * dort. Der Abreisetag bekommt deshalb die Diagonale des Buchungskalenders — sie ist keine Optik,
 * sondern die Aussage „hier fährt am Vormittag noch jemand raus".
 *
 * Sie steht **immer, wenn der Zeitraum endet**, nicht nur bei nahtlosem Anschluss: der Wagen
 * bleibt bis zur Check-out-Zeit stehen, ob ein Nachfolger kommt oder nicht. Die Gastfläche hatte
 * diese Semantik von Anfang an, diese hier die Reinigungs-Logik der Buchungen geerbt — beide
 * Seiten hatten es als Test festgeschrieben, mit entgegengesetztem Ergebnis. Der PO hat die
 * Gast-Semantik bestätigt.
 */
export function buildParkingSpans(
  reservations: ParkingOccupancyEntry[],
  dates: string[],
): ParkingSpan[] {
  if (dates.length === 0) return [];
  const spans: ParkingSpan[] = [];

  for (const entry of reservations) {
    const start = clampStart(entry.startDate, dates);
    const end = clampEnd(entry.endDate, dates);
    if (end <= start) continue;
    spans.push({
      entry,
      startIndex: start,
      nights: end - start,
      hasTransitionIn: false,
      // Nur wenn der Abreisetag im Fenster liegt — sonst hinge der Balken über den Rand des
      // Rasters hinaus.
      hasTransitionOut: end < dates.length,
    });
  }

  spans.sort((left, right) => left.startIndex - right.startIndex);
  for (let index = 0; index < spans.length - 1; index += 1) {
    const current = spans[index];
    const next = spans[index + 1];
    // Nahtlos: der erste Tag des Nächsten ist der Abreisetag des Vorigen. Dann teilen sich beide
    // die Zelle — der eine fährt vormittags raus, der andere zieht nachmittags ein.
    if (current && next && next.startIndex === current.startIndex + current.nights) {
      next.hasTransitionIn = true;
    }
  }
  return spans;
}

/** Erster sichtbarer Tag der Reservierung; alles davor liegt links außerhalb des Fensters. */
function clampStart(startDate: string, dates: string[]): number {
  const index = dates.findIndex((date) => date >= startDate);
  return index === -1 ? dates.length : index;
}

/** Erster Tag NACH der Reservierung — exklusiv, wie der Zeitraum selbst. */
function clampEnd(endDate: string, dates: string[]): number {
  const index = dates.findIndex((date) => date >= endDate);
  return index === -1 ? dates.length : index;
}

/** Zusammenhängende freie Tage im Fenster als [erster, letzter] – ein Tag ist frei, wenn keine
 *  Belegung ihn deckt (der Abreisetag gehört schon wieder dem Nächsten). */
export function freeRanges(
  reservations: ParkingOccupancyEntry[],
  dates: string[],
): [string, string][] {
  const ranges: [string, string][] = [];
  for (const date of dates) {
    const taken = reservations.some((entry) => entry.startDate <= date && date < entry.endDate);
    const last = ranges.at(-1);
    if (taken) continue;
    if (last && dates.indexOf(last[1]) === dates.indexOf(date) - 1) last[1] = date;
    else ranges.push([date, date]);
  }
  return ranges;
}

/**
 * Belegte Tage im Fenster. Sperren zählen mit: der Platz ist nicht verkaufbar, und genau danach
 * fragt die Zahl.
 */
export function occupiedDayCount(spans: ParkingSpan[]): number {
  return spans.reduce((total, span) => total + span.nights, 0);
}

/**
 * Was auf dem Balken steht (Board „Belegung · Stellplätze“): „Kennzeichen · Gast“, ohne Kennzeichen
 * „Ohne Kennzeichen · Gast“, bei einer Sperre „Gesperrt · Notiz“. Das Kennzeichen zuerst – es ist der
 * Grund, warum jemand hier nachsieht: im Hof steht ein Wagen, darf er dort stehen?
 */
export function spanLabel(entry: ParkingOccupancyEntry): string {
  const who = entry.guestName ?? entry.bookingReference ?? entry.propertyName;
  const head = entry.kind === 'Block' ? 'Gesperrt' : (entry.licensePlate ?? 'Ohne Kennzeichen');
  return who ? `${head} · ${who}` : head;
}

export type ParkingState = 'Bezahlt' | 'Ohne Zahlung' | 'Gehalten' | 'Gesperrt';

/**
 * Stand einer Belegung. `paid` sagt, ob Geld daran hängt – nicht `status`: eine Sperre ist
 * `Confirmed` und nie bezahlt, und zwischen Zahlung und Bestätigung steht eine Gastzeile auf
 * `Pending`.
 */
export function parkingState(entry: ParkingOccupancyEntry): ParkingState {
  if (entry.kind === 'Block') return 'Gesperrt';
  if (entry.status === 'Pending') return 'Gehalten';
  return entry.paid ? 'Bezahlt' : 'Ohne Zahlung';
}

/**
 * Drei Töne für Gastzeilen und die Schraffur der Sperre – ohne den Unterschied liest der Betreiber
 * eine ausgelastete Woche, die keine ist.
 */
export const PARKING_TONE: Record<ParkingState, string> = {
  Bezahlt: 'bg-forest-soft text-forest-deep',
  'Ohne Zahlung': 'bg-card-alt text-ink',
  Gehalten: 'bg-amber-tint text-amber-ink',
  Gesperrt:
    'bg-[repeating-linear-gradient(135deg,var(--color-sand)_0_6px,var(--color-paper)_6px_12px)] text-ink-soft',
};
